/**
 * Sector radar — pinned sector/category map plus pure metric functions: MC-weighted sector
 * returns, breadth vs BTC, relative strength, leaders/laggards, emerging categories, chain
 * signals. Pure: no IO, no Date.now() — callers pass series/reference values explicitly.
 */

export const SECTOR_CATEGORIES = [
  { label: "L1", categoryName: "Layer 1 (L1)" },
  { label: "L2", categoryName: "Layer 2 (L2)" },
  { label: "DeFi", categoryName: "Decentralized Finance (DeFi)" },
  { label: "RWA", categoryName: "Real World Assets (RWA)" },
  { label: "AI", categoryName: "Artificial Intelligence (AI)" },
  { label: "Oracle", categoryName: "Oracle" },
  { label: "DEX", categoryName: "Decentralized Exchange (DEX)" },
  { label: "Meme", categoryName: "Meme" },
  { label: "Gaming", categoryName: "Gaming (GameFi)" },
  { label: "LSD", categoryName: "Liquid Staking" },
  { label: "Modular", categoryName: "Modular Blockchain" },
  { label: "Infra", categoryName: "Infrastructure" },
  { label: "Restaking", categoryName: "Restaking" },
  { label: "Perps", categoryName: "Perpetuals" },
  { label: "Privacy", categoryName: "Privacy Coins" },
] as const;

export type SectorLabel = (typeof SECTOR_CATEGORIES)[number]["label"];

export const SECTOR_LABELS: readonly SectorLabel[] = SECTOR_CATEGORIES.map((s) => s.label);

/** Extra category names that map to a label for token tagging only (not tracked by the radar). */
export const SECTOR_ALIASES: Readonly<Record<string, SectorLabel>> = { "Smart Contract Platform": "L1" };

const CATEGORY_NAME_TO_LABEL: ReadonlyMap<string, SectorLabel> = new Map([
  ...SECTOR_CATEGORIES.map((s): [string, SectorLabel] => [s.categoryName, s.label]),
  ...Object.entries(SECTOR_ALIASES),
]);

const SECTOR_LABEL_SET: ReadonlySet<string> = new Set(SECTOR_LABELS);

export function isSectorLabel(v: string): v is SectorLabel {
  return SECTOR_LABEL_SET.has(v);
}

/** CoinGecko category names -> short watchlist sector labels, first-seen order, max 2. */
export function mapSectors(categoryNames: readonly string[]): SectorLabel[] {
  const out: SectorLabel[] = [];
  for (const name of categoryNames) {
    const mapped = CATEGORY_NAME_TO_LABEL.get(name);
    if (mapped && !out.includes(mapped)) out.push(mapped);
    if (out.length >= 2) break;
  }
  return out;
}

export interface MemberSnapshot {
  readonly cgId: string;
  readonly symbol: string;
  readonly name: string;
  readonly marketCap: number | null;
  readonly change7d: number | null;
  readonly change30d: number | null;
}

export interface ReferenceReturns {
  readonly btc7d: number | null;
  readonly btc30d: number | null;
  readonly total3_7d: number | null;
  readonly total3_30d: number | null;
}

export interface CategorySnapshot {
  readonly id: string;
  readonly name: string;
  readonly marketCap: number;
  readonly volume24h: number | null;
  readonly change24h: number | null;
  readonly change7d: number | null;
  readonly top3: string[];
}

export type ChainSignal = "stables_inflow" | "tvl_inflow" | "outflow";

export const EMERGING_MIN_MARKET_CAP_USD = 250_000_000;
export const EMERGING_NAME_DENYLIST =
  /ecosystem|launchpad|index|portfolio|exchange-based|wrapped|stablecoin/i;

const EXCLUDED_MEMBER_IDS = new Set(["bitcoin", "ethereum"]);
/** Cap on each member's weight share when computing weightedReturn. */
const MEMBER_WEIGHT_CAP = 0.2;
/** Minimum eligible members for weightedReturn to return a value. */
const MIN_ELIGIBLE_MEMBERS = 3;

function changeOf(member: MemberSnapshot, window: "7d" | "30d"): number | null {
  return window === "7d" ? member.change7d : member.change30d;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Members eligible for return/breadth metrics: excludes bitcoin/ethereum, null change, null/0 marketCap. */
export function eligibleMembers(members: readonly MemberSnapshot[], window: "7d" | "30d"): MemberSnapshot[] {
  return members.filter((m) => {
    if (EXCLUDED_MEMBER_IDS.has(m.cgId)) return false;
    if (changeOf(m, window) == null) return false;
    if (m.marketCap == null || m.marketCap === 0) return false;
    return true;
  });
}

/**
 * MC-weighted mean of member returns (percent). Excludes bitcoin/ethereum and members with
 * null change or null/0 marketCap. Each weight is capped at max(20%, 1/n) of the total (excess
 * redistributed proportionally to the uncapped members) so one mega-cap cannot define the
 * sector. Null when fewer than 3 eligible members.
 */
export function weightedReturn(members: readonly MemberSnapshot[], window: "7d" | "30d"): number | null {
  const eligible = eligibleMembers(members, window);
  if (eligible.length < MIN_ELIGIBLE_MEMBERS) return null;

  const totalMc = eligible.reduce((sum, m) => sum + (m.marketCap ?? 0), 0);
  if (totalMc <= 0) return null;

  const weights = eligible.map((m) => (m.marketCap ?? 0) / totalMc);
  // Water-filling: cap weights above the threshold and hand the excess proportionally to the
  // uncapped members, repeating until nothing exceeds the cap. The effective cap is never below
  // 1/n, so a solution always exists (n equal members simply stay equal-weighted).
  const cap = Math.max(MEMBER_WEIGHT_CAP, 1 / weights.length);
  const capped = new Array<boolean>(weights.length).fill(false);
  for (;;) {
    let excess = 0;
    for (let i = 0; i < weights.length; i++) {
      const w = weights[i] ?? 0;
      if (!capped[i] && w > cap + 1e-12) {
        excess += w - cap;
        weights[i] = cap;
        capped[i] = true;
      }
    }
    if (excess === 0) break;
    const receiverTotal = weights.reduce((sum, w, i) => (capped[i] ? sum : sum + (w ?? 0)), 0);
    if (receiverTotal <= 0) break;
    for (let i = 0; i < weights.length; i++) {
      if (!capped[i]) weights[i] = (weights[i] ?? 0) + excess * ((weights[i] ?? 0) / receiverTotal);
    }
  }

  const weighted = eligible.reduce((sum, m, i) => sum + (weights[i] ?? 0) * (changeOf(m, window) ?? 0), 0);
  return round2(weighted);
}

/** Percent of eligible members whose 30d change beats btc30d (null when btc30d null). */
export function breadthVsBtc(members: readonly MemberSnapshot[], btc30d: number | null): number | null {
  if (btc30d == null) return null;
  const eligible = eligibleMembers(members, "30d");
  if (eligible.length === 0) return null;
  const beating = eligible.filter((m) => (m.change30d ?? 0) > btc30d).length;
  return round2((beating / eligible.length) * 100);
}

/** Percent of eligible members with change7d > 0. */
export function breadthPositive7d(members: readonly MemberSnapshot[]): number | null {
  const eligible = eligibleMembers(members, "7d");
  if (eligible.length === 0) return null;
  const positive = eligible.filter((m) => (m.change7d ?? 0) > 0).length;
  return round2((positive / eligible.length) * 100);
}

/** sectorReturn - reference, or null when either is null. */
export function relativeStrength(sectorReturn: number | null, reference: number | null): number | null {
  if (sectorReturn == null || reference == null) return null;
  return round2(sectorReturn - reference);
}

/** Top/bottom n eligible members by change30d (null changes sort last and are excluded). */
export function leadersAndLaggards(
  members: readonly MemberSnapshot[],
  n = 5,
): { leaders: MemberSnapshot[]; laggards: MemberSnapshot[] } {
  const eligible = eligibleMembers(members, "30d");
  const sorted = [...eligible].sort((a, z) => (z.change30d ?? 0) - (a.change30d ?? 0));
  const leaders = sorted.slice(0, n);
  const laggards = sorted.slice(-n).reverse();
  return { leaders, laggards };
}

/** volume24h / marketCap, null when either is null/0. */
export function volumeToMc(volume24h: number | null, marketCap: number | null): number | null {
  if (volume24h == null || marketCap == null || marketCap === 0) return null;
  return volume24h / marketCap;
}

/**
 * Emerging-category filter for a category list: not in SECTOR_CATEGORIES/SECTOR_ALIASES,
 * marketCap >= minMarketCapUsd (default 250_000_000), name does not match the denylist.
 * Sorted by change7d desc when available for the row, else change24h desc.
 */
export function emergingCategories(
  rows: readonly CategorySnapshot[],
  opts?: { minMarketCapUsd?: number; limit?: number },
): CategorySnapshot[] {
  const minMarketCapUsd = opts?.minMarketCapUsd ?? EMERGING_MIN_MARKET_CAP_USD;
  const filtered = rows.filter((r) => {
    if (CATEGORY_NAME_TO_LABEL.has(r.name)) return false;
    if (r.marketCap < minMarketCapUsd) return false;
    if (EMERGING_NAME_DENYLIST.test(r.name)) return false;
    return true;
  });
  const sortKey = (r: CategorySnapshot): number | null => r.change7d ?? r.change24h;
  const sorted = [...filtered].sort((a, z) => {
    const av = sortKey(a);
    const zv = sortKey(z);
    if (av == null && zv == null) return 0;
    if (av == null) return 1;
    if (zv == null) return -1;
    return zv - av;
  });
  return opts?.limit != null ? sorted.slice(0, opts.limit) : sorted;
}

/**
 * Percent change between the latest value and the value `days` earlier in a daily series
 * (closest row at or before that day); null when missing.
 */
export function seriesChange(
  series: readonly { day: string; value: number | null }[],
  days: number,
): number | null {
  if (series.length === 0) return null;
  const sorted = [...series].sort((a, z) => (a.day < z.day ? -1 : a.day > z.day ? 1 : 0));
  const latest = sorted[sorted.length - 1];
  if (latest == null || latest.value == null) return null;

  const latestMs = new Date(`${latest.day}T00:00:00Z`).getTime();
  const targetMs = latestMs - days * 86_400_000;

  let earlier: { day: string; value: number | null } | null = null;
  for (const point of sorted) {
    const ms = new Date(`${point.day}T00:00:00Z`).getTime();
    if (ms > targetMs) continue;
    if (earlier == null || point.day > earlier.day) earlier = point;
  }
  if (earlier == null || earlier.value == null || earlier.value === 0) return null;

  return round2(((latest.value - earlier.value) / earlier.value) * 100);
}

/** Chain signal thresholds, in percent. */
const STABLES_INFLOW_MIN = 5;
const STABLES_INFLOW_TVL_MAX = 2;
const TVL_INFLOW_MIN = 10;
const OUTFLOW_MAX = -5;

/**
 * Chain signal: "stables_inflow" when stables 7d change >= +5% and TVL 7d change <= +2%;
 * "tvl_inflow" when TVL 7d >= +10%; "outflow" when both 7d changes <= -5%; else null.
 */
export function chainSignal(tvl7d: number | null, stables7d: number | null): ChainSignal | null {
  if (tvl7d == null || stables7d == null) return null;
  if (stables7d >= STABLES_INFLOW_MIN && tvl7d <= STABLES_INFLOW_TVL_MAX) return "stables_inflow";
  if (tvl7d >= TVL_INFLOW_MIN) return "tvl_inflow";
  if (tvl7d <= OUTFLOW_MAX && stables7d <= OUTFLOW_MAX) return "outflow";
  return null;
}
